import * as React from 'react';
import { MuiMaterials } from '../@index.js';
import * as global from '../data/global';

const siteRowId = (row) => String(
    row?.customerSiteGuid ?? row?.CustomerSiteGuid ?? row?.siteId ?? row?.SiteId ?? ''
);

const siteRowName = (row) => row?.siteName ?? row?.SiteName ?? '';

const MultipleSiteSelect = ({ names = [], value = [], onChange }) => {
    const selectedIds = React.useMemo(
        () => (Array.isArray(value) ? value : []).map((id) => String(id)).filter(Boolean),
        [value]
    );
    const selectedSet = React.useMemo(() => new Set(selectedIds), [selectedIds]);

    const duplicateNames = React.useMemo(() => {
        const counts = new Map();
        (names || []).forEach((row) => {
            const label = siteRowName(row);
            counts.set(label, (counts.get(label) || 0) + 1);
        });
        return counts;
    }, [names]);

    const formatSiteLabel = React.useCallback((row) => {
        const id = siteRowId(row);
        const label = siteRowName(row);
        if ((duplicateNames.get(label) || 0) > 1 && id.length >= 8) {
            return `${label} [${id.slice(0, 8)}…]`;
        }
        return label;
    }, [duplicateNames]);

    const sortedRows = React.useMemo(() => {
        return [...(names || [])]
            .filter((row) => siteRowId(row))
            .sort((a, b) =>
                formatSiteLabel(a).localeCompare(formatSiteLabel(b), undefined, {
                    sensitivity: 'base',
                    numeric: true,
                })
            );
    }, [names, formatSiteLabel]);

    const toggleSite = (id, checked) => {
        const next = checked
            ? [...selectedIds, id].filter((x, i, arr) => arr.indexOf(x) === i)
            : selectedIds.filter((x) => x !== id);
        if (typeof onChange === 'function') {
            onChange(next);
        }
    };

    const textSecondary = global.gw.theme === 'light' ? '#666666' : '#c4a574';
    const tileBorder = global.gw.theme === 'light' ? '#e0e0e0' : '#3d3d3d';

    return (
        <MuiMaterials.Box sx={{ width: '100%' }}>
            <MuiMaterials.Typography
                variant="subtitle2"
                sx={{ mb: 1, fontWeight: 600 }}
            >
                Sites
                {sortedRows.length > 0 ? (
                    <MuiMaterials.Typography
                        component="span"
                        variant="body2"
                        sx={{ ml: 1, color: textSecondary, fontWeight: 400 }}
                    >
                        {selectedIds.length} of {sortedRows.length} selected
                    </MuiMaterials.Typography>
                ) : null}
            </MuiMaterials.Typography>
            {sortedRows.length === 0 ? (
                <MuiMaterials.Typography variant="body2" sx={{ color: textSecondary }}>
                    No sites available.
                </MuiMaterials.Typography>
            ) : (
                <MuiMaterials.Box
                    sx={{
                        display: 'grid',
                        gap: 0.5,
                        width: '100%',
                        gridTemplateColumns: {
                            xs: 'repeat(1, minmax(0, 1fr))',
                            sm: 'repeat(2, minmax(0, 1fr))',
                            md: 'repeat(3, minmax(0, 1fr))',
                            lg: 'repeat(4, minmax(0, 1fr))',
                        },
                    }}
                >
                    {sortedRows.map((row) => {
                        const id = siteRowId(row);
                        const checked = selectedSet.has(id);
                        return (
                            <MuiMaterials.FormControlLabel
                                key={id}
                                sx={{
                                    m: 0,
                                    minWidth: 0,
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 1,
                                    border: `1px solid ${checked ? tileBorder : 'transparent'}`,
                                    alignItems: 'center',
                                    '& .MuiFormControlLabel-label': {
                                        minWidth: 0,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        fontSize: '14px',
                                    },
                                }}
                                control={
                                    <MuiMaterials.Checkbox
                                        size="small"
                                        checked={checked}
                                        onChange={(e) => toggleSite(id, e.target.checked)}
                                    />
                                }
                                label={formatSiteLabel(row)}
                            />
                        );
                    })}
                </MuiMaterials.Box>
            )}
        </MuiMaterials.Box>
    );
};

export default MultipleSiteSelect;
